/**
 * InfinitSales - Follow-ups Page
 * Manage follow-up challenge sequences (e.g., 21-day diet challenge)
 * Supports creating, editing, testing, and activating follow-up sequences.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Zap, Plus, Search, ChevronLeft, ChevronRight, Clock,
  Play, Pause, Trash2, X, Users, FileText,
  Image, Video, Music, File, Upload, Check,
  Loader2, Calendar, ChevronDown, ChevronUp,
  PanelLeftOpen, PanelLeftClose, MessageSquare,
  Sparkles, AlertTriangle, Lock, LayoutGrid, List,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import followupService from '../../services/global/followupService';
import fileUploadService from '../../services/global/fileUploadService';
import whatsappService from '../../services/wa-personal/whatsappService';
import waBusinessService from '../../services/wa-business/waBusinessService';
import templateService from '../../services/global/templateService';
import { positionalParamsInText, fillBodyText } from '../../utils/templateVariables';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplateMessageField from '../../components/messages/TemplateMessageField';
import VariableChips from '../../components/messages/VariableChips';
import {
  analyzeTemplate, findTemplate, uniqueApproved, templateCapabilities,
  missingValues, valuesFromMessage,
} from '../../utils/templateParams';
import subscriptionService from '../../services/global/subscriptionService';
import PromoModal from '../../components/PromoModal';
import DetailsTab from '../../components/followups/DetailsTab';
import ScheduledMessageDetailsTab from '../../components/followups/ScheduledMessageDetailsTab';
import RecipientsTab from '../../components/followups/RecipientsTab';
import HistoryTab from '../../components/followups/HistoryTab';
import { followupPhoneFromStored, followupPhoneToRaw } from '../../components/followups/followupPhone';




// ============================================================
// HELPER: Empty State
// ============================================================
const EmptyState = ({ colors, onCreateNew }) => (
  <div style={{
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: '16px', padding: '40px',
  }}>
    <div style={{
      width: '80px', height: '80px', borderRadius: '20px',
      backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Zap size={36} color={colors.accentPrimary} />
    </div>
    <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
      Nenhum follow-up criado
    </h3>
    <p style={{ fontSize: '14px', color: colors.textSecondary, textAlign: 'center', maxWidth: '360px', margin: 0 }}>
      Crie sequências de mensagens para enviar automaticamente aos seus contatos ao longo dos dias.
    </p>
    <button
      onClick={onCreateNew}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
        backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
        borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
      }}
    >
      <Plus size={18} /> Novo Follow-up
    </button>
  </div>
);


// ============================================================
// HELPER: Status Badge
// ============================================================
const StatusBadge = ({ status, colors }) => {
  const config = {
    draft: { label: 'Rascunho', bg: `${colors.textSecondary}20`, color: colors.textSecondary },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    paused: { label: 'Pausado', bg: '#f59e0b20', color: '#f59e0b' },
    completed: { label: 'Concluído', bg: '#6366f120', color: '#6366f1' },
  };
  const c = config[status] || config.draft;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};


// ============================================================
// HELPER: File type icon
// ============================================================
const FileTypeIcon = ({ type, size = 16 }) => {
  switch (type) {
    case 'image': return <Image size={size} />;
    case 'video': return <Video size={size} />;
    case 'audio': return <Music size={size} />;
    default: return <File size={size} />;
  }
};


// ============================================================
// SUB: Day Card (week view)
// ============================================================
const DayCard = ({ dayNumber, totalDays, messages, colors, onClick }) => {
  const isEnabled = dayNumber <= totalDays;
  const msgCount = messages.length;
  const hasFiles = messages.some(m => m.files && m.files.length > 0);
  const firstMsg = messages[0];

  return (
    <div
      onClick={isEnabled ? onClick : undefined}
      style={{
        backgroundColor: isEnabled ? colors.bgPrimary : `${colors.bgTertiary}50`,
        border: `1px solid ${isEnabled ? colors.borderColor : 'transparent'}`,
        borderRadius: '12px', padding: '12px', minHeight: '120px',
        cursor: isEnabled ? 'pointer' : 'default',
        opacity: isEnabled ? 1 : 0.4,
        transition: 'all 0.15s',
        display: 'flex', flexDirection: 'column', gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          width: '28px', height: '28px', borderRadius: '50%',
          backgroundColor: msgCount > 0 ? colors.accentPrimary : `${colors.textSecondary}20`,
          color: msgCount > 0 ? 'white' : colors.textSecondary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '12px', fontWeight: 600,
        }}>
          {dayNumber}
        </span>
        {msgCount > 0 && (
          <span style={{
            fontSize: '11px', color: colors.accentPrimary, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '3px',
          }}>
            <MessageSquare size={12} /> {msgCount}
          </span>
        )}
      </div>

      {firstMsg ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <p style={{
            fontSize: '11px', color: colors.textSecondary, margin: 0,
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <Clock size={10} /> {firstMsg.send_time}
          </p>
          {firstMsg.text && (
            <p style={{
              fontSize: '12px', color: colors.textPrimary, margin: '4px 0 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {firstMsg.text.slice(0, 40)}{firstMsg.text.length > 40 ? '...' : ''}
            </p>
          )}
          {hasFiles && (
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {messages.flatMap(m => m.files || []).slice(0, 3).map((f, i) => (
                <span key={i} style={{ color: colors.textSecondary }}>
                  <FileTypeIcon type={f.type} size={12} />
                </span>
              ))}
            </div>
          )}
        </div>
      ) : isEnabled ? (
        <p style={{ fontSize: '11px', color: colors.textTertiary, margin: 0, fontStyle: 'italic' }}>
          Clique para configurar
        </p>
      ) : null}
    </div>
  );
};


// ============================================================
// SUB: Day Editor Modal
// ============================================================
const DayEditorModal = ({ isOpen, dayNumber, messages, onSave, onClose, colors, isBusinessChannel, approvedTemplates = [], canCreateAiGenerated = false }) => {
  const [localMessages, setLocalMessages] = useState([]);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});
  // Textareas por mensagem: a variável entra onde está o cursor.
  const textRefs = useRef({});
  // Seletor único e editor de variáveis (2026-09-14). Sem a flag, como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);
  const followupCaps = templateCapabilities('followup', flags);

  useEffect(() => {
    if (isOpen) {
      setLocalMessages(messages.map(m => ({ ...m, files: [...(m.files || [])] })));
    }
  }, [isOpen, messages]);

  if (!isOpen) return null;

  const addMessage = () => {
    if (localMessages.length >= 20) return;
    setLocalMessages(prev => [...prev, {
      id: null,
      order: prev.length + 1,
      send_time: '10:00',
      text: '',
      files: [],
      message_type: 'custom',
      template_name: '',
      template_language: 'pt_BR',
      interactive_type: 'button',
      buttons: [],
      list_button_text: 'Ver opções',
      list_sections: [{ title: 'Opções', rows: [] }],
      header_text: '',
      footer_text: '',
      // ai_generated defaults
      instruction: '',
      use_conversation_context: true,
      allow_agent_skip: false,
      force_response: false,
      context_message_limit: 15,
    }]);
  };

  // Chip de variável: insere o token na posição do cursor do campo (mesmo
  // comportamento dos composers do inbox). O backend resolve por contato no
  // envio, nos 4 canais.
  const insertVariable = (index, field, token) => {
    const el = textRefs.current[`${index}:${field}`];
    setLocalMessages(prev => prev.map((m, i) => {
      if (i !== index) return m;
      const cur = m[field] || '';
      const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
      const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : cur.length;
      return { ...m, [field]: cur.slice(0, start) + token + cur.slice(end) };
    }));
  };

  const updateMessage = (index, field, value) => {
    setLocalMessages(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const removeMessage = (index) => {
    setLocalMessages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  const moveMessage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= localMessages.length) return;
    setLocalMessages(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  const handleFileUpload = async (msgIndex, files) => {
    const uploadKey = `msg-${msgIndex}`;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));

    try {
      for (const file of files) {
        const validation = fileUploadService.validateFile(file);
        if (!validation.valid) continue;

        const result = await fileUploadService.uploadFile(file);
        if (result) {
          setLocalMessages(prev => prev.map((m, i) => {
            if (i !== msgIndex) return m;
            return {
              ...m,
              files: [...m.files, {
                url: result.url,
                type: result.type,
                filename: result.filename,
                mimeType: result.mimeType,
              }],
            };
          }));
        }
      }
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = (msgIndex, fileIndex) => {
    setLocalMessages(prev => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      return { ...m, files: m.files.filter((_, fi) => fi !== fileIndex) };
    }));
  };

  const handleSave = () => {
    // Validate: each message must have content based on type
    const valid = localMessages.every(m => {
      if (m.message_type === 'template' && templateUi) {
        const tpl = findTemplate(uniqueApproved(approvedTemplates), m.template_name, m.template_language);
        return Boolean(tpl)
          && missingValues(analyzeTemplate(tpl), valuesFromMessage(m), followupCaps).length === 0;
      }
      if (m.message_type === 'template') return m.template_name && m.template_name.trim();
      if (m.message_type === 'interactive') {
        if (!m.text || !m.text.trim()) return false;
        if (m.interactive_type === 'button') {
          return m.buttons && m.buttons.length >= 1 && m.buttons.length <= 3
            && m.buttons.every(b => b.title && b.title.trim() && b.title.length <= 20);
        } else if (m.interactive_type === 'list') {
          const allRows = (m.list_sections || []).flatMap(s => s.rows || []);
          return allRows.length >= 1 && allRows.length <= 10
            && allRows.every(r => r.title && r.title.trim() && r.title.length <= 24);
        }
        return false;
      }
      if (m.message_type === 'ai_generated') {
        return m.instruction && m.instruction.trim().length >= 10;
      }
      return (m.text && m.text.trim()) || (m.files && m.files.length > 0);
    });
    if (!valid && localMessages.length > 0) {
      alert('Verifique as mensagens: custom precisa de texto/arquivo, template precisa estar selecionado, interativa precisa de texto + botões/opções válidos, mensagem com IA precisa de instrução com no mínimo 10 caracteres.');
      return;
    }
    // Auto-fill defaults and strip UI-only fields before saving
    const cleaned = localMessages.map(m => {
      const msg = { ...m };
      delete msg._showAdvanced;
      if (msg.message_type === 'interactive' && msg.interactive_type === 'list') {
        if (!msg.list_button_text || !msg.list_button_text.trim()) {
          msg.list_button_text = 'Ver opções';
        }
        // Ensure first section has a title
        if (msg.list_sections && msg.list_sections[0] && !msg.list_sections[0].title) {
          msg.list_sections[0].title = 'Opções';
        }
      }
      return msg;
    });
    onSave(cleaned);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '640px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              {dayNumber === 0 ? 'Mensagens Agendadas' : `Dia ${dayNumber} - Mensagens`}
            </h3>
            <p style={{ margin: 0, fontSize: '12px', color: colors.textSecondary }}>
              {localMessages.length}/20 mensagens configuradas
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Messages list */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {localMessages.length === 0 && (
            <p style={{ color: colors.textSecondary, fontSize: '14px', textAlign: 'center', padding: '24px 0' }}>
              Nenhuma mensagem configurada para este dia.
            </p>
          )}

          {localMessages.map((msg, idx) => (
            <div key={idx} style={{
              backgroundColor: colors.bgSecondary, borderRadius: '12px', padding: '14px',
              border: `1px solid ${colors.borderColor}`,
              overflow: 'hidden', wordBreak: 'break-word', minWidth: 0,
              flexShrink: 0,
            }}>
              {/* Message header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                    Msg {idx + 1}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Clock size={14} color={colors.textSecondary} />
                    <input
                      type="time"
                      value={msg.send_time}
                      onChange={(e) => updateMessage(idx, 'send_time', e.target.value)}
                      style={{
                        backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                        padding: '4px 8px', fontSize: '13px', outline: 'none',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => moveMessage(idx, -1)} disabled={idx === 0} style={{
                    background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer',
                    color: idx === 0 ? colors.textTertiary : colors.textSecondary, padding: '4px',
                  }}>
                    <ChevronUp size={16} />
                  </button>
                  <button onClick={() => moveMessage(idx, 1)} disabled={idx === localMessages.length - 1} style={{
                    background: 'none', border: 'none',
                    cursor: idx === localMessages.length - 1 ? 'default' : 'pointer',
                    color: idx === localMessages.length - 1 ? colors.textTertiary : colors.textSecondary, padding: '4px',
                  }}>
                    <ChevronDown size={16} />
                  </button>
                  <button onClick={() => removeMessage(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#ef4444', padding: '4px',
                  }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Message Type Toggle */}
              {(isBusinessChannel || canCreateAiGenerated) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' }}>
                  <button
                    onClick={() => updateMessage(idx, 'message_type', 'custom')}
                    style={{
                      flex: '1 1 100px', padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: 500, cursor: 'pointer', border: 'none',
                      backgroundColor: (msg.message_type || 'custom') === 'custom' ? colors.accentPrimary : colors.bgPrimary,
                      color: (msg.message_type || 'custom') === 'custom' ? '#fff' : colors.textSecondary,
                    }}
                  >
                    {isBusinessChannel ? 'Mensagem Custom' : 'Texto'}
                  </button>
                  {isBusinessChannel && (
                    <>
                      <button
                        onClick={() => updateMessage(idx, 'message_type', 'template')}
                        style={{
                          flex: '1 1 100px', padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                          fontWeight: 500, cursor: 'pointer', border: 'none',
                          backgroundColor: msg.message_type === 'template' ? colors.accentPrimary : colors.bgPrimary,
                          color: msg.message_type === 'template' ? '#fff' : colors.textSecondary,
                        }}
                      >
                        Template Meta
                      </button>
                      <button
                        onClick={() => updateMessage(idx, 'message_type', 'interactive')}
                        style={{
                          flex: '1 1 100px', padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                          fontWeight: 500, cursor: 'pointer', border: 'none',
                          backgroundColor: msg.message_type === 'interactive' ? colors.accentPrimary : colors.bgPrimary,
                          color: msg.message_type === 'interactive' ? '#fff' : colors.textSecondary,
                        }}
                      >
                        Interativa
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (!canCreateAiGenerated) {
                        alert('Mensagens com IA estão disponíveis a partir do plano Pro.');
                        return;
                      }
                      updateMessage(idx, 'message_type', 'ai_generated');
                    }}
                    title={canCreateAiGenerated ? '' : 'Disponível a partir do plano Pro'}
                    style={{
                      flex: '1 1 100px', padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: 500, cursor: canCreateAiGenerated ? 'pointer' : 'not-allowed',
                      border: 'none',
                      backgroundColor: msg.message_type === 'ai_generated' ? '#a855f7' :
                        (canCreateAiGenerated ? colors.bgPrimary : colors.bgPrimary),
                      color: msg.message_type === 'ai_generated' ? '#fff' :
                        (canCreateAiGenerated ? '#a855f7' : colors.textTertiary),
                      opacity: canCreateAiGenerated ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}
                  >
                    {!canCreateAiGenerated && <Lock size={11} />}
                    <Sparkles size={12} /> Mensagem com IA
                  </button>
                </div>
              )}

              {/* AI-generated message editor */}
              {msg.message_type === 'ai_generated' ? (
                <div style={{
                  borderLeft: '3px solid #a855f7',
                  paddingLeft: '12px',
                  display: 'flex', flexDirection: 'column', gap: '10px',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '11px', color: '#a855f7', fontWeight: 500,
                  }}>
                    <Sparkles size={13} /> A mensagem será gerada pelo seu agente no momento do envio
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', color: colors.textSecondary, fontWeight: 500 }}>
                      Instrução para a IA *
                    </label>
                    <textarea
                      value={msg.instruction || ''}
                      onChange={(e) => updateMessage(idx, 'instruction', e.target.value)}
                      placeholder="Ex: Faça uma saudação personalizada usando o primeiro nome do contato. Pergunte se ele tem dúvida sobre o produto X."
                      rows={4}
                      maxLength={2000}
                      style={{
                        marginTop: '4px',
                        width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                        fontFamily: 'inherit', boxSizing: 'border-box',
                        wordBreak: 'break-word', overflowWrap: 'break-word',
                      }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                      <span style={{ fontSize: '10px', color: colors.textTertiary }}>
                        Descreva o que a IA deve dizer. Quanto mais específico, melhor.
                      </span>
                      <span style={{ fontSize: '10px', color: colors.textTertiary }}>
                        {(msg.instruction || '').length}/2000
                      </span>
                    </div>
                  </div>

                  <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.textPrimary }}>
                    <input
                      type="checkbox"
                      checked={msg.use_conversation_context !== false}
                      onChange={(e) => updateMessage(idx, 'use_conversation_context', e.target.checked)}
                      style={{ marginTop: '2px' }}
                    />
                    <span>
                      <strong>Considerar contexto da conversa</strong>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                        A IA terá acesso às últimas 15 mensagens trocadas com este contato.
                      </div>
                    </span>
                  </label>

                  <label style={{
                    display: 'flex', gap: '8px', fontSize: '12px', color: colors.textPrimary,
                    cursor: msg.force_response ? 'not-allowed' : 'pointer',
                    opacity: msg.force_response ? 0.55 : 1,
                  }}>
                    <input
                      type="checkbox"
                      checked={!!msg.allow_agent_skip && !msg.force_response}
                      disabled={!!msg.force_response}
                      onChange={(e) => updateMessage(idx, 'allow_agent_skip', e.target.checked)}
                      style={{ marginTop: '2px' }}
                    />
                    <span>
                      <strong>Permitir que a IA decida não enviar</strong>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                        {msg.force_response
                          ? 'Desligado: "Enviar mesmo em atendimento humano" obriga a IA a escrever.'
                          : 'Se marcado, a IA pode escolher não enviar a mensagem caso julgue inadequado.'}
                      </div>
                    </span>
                  </label>

                  <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.textPrimary }}>
                    <input
                      type="checkbox"
                      checked={!!msg.force_response}
                      onChange={(e) => {
                        const on = e.target.checked;
                        updateMessage(idx, 'force_response', on);
                        if (on) updateMessage(idx, 'allow_agent_skip', false);
                      }}
                      style={{ marginTop: '2px' }}
                    />
                    <span>
                      <strong>Enviar mesmo para contatos em atendimento humano</strong>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px', lineHeight: 1.45 }}>
                        Por padrão, a IA não escreve para quem tem um vendedor como responsável,
                        está com o atendimento pausado ou é número da equipe. Esses contatos aparecem
                        no histórico como <em>Pulada: cliente em atendimento humano</em>. Marque para
                        a IA escrever mesmo assim, pelo agente principal da conta. Contato transferido
                        para outro workspace e lista de bloqueio continuam fora.
                      </div>
                    </span>
                  </label>

                  {isBusinessChannel && (
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: '8px',
                      padding: '10px 12px',
                      backgroundColor: 'rgba(245, 158, 11, 0.12)',
                      border: '1px solid rgba(245, 158, 11, 0.45)',
                      borderRadius: '8px',
                      fontSize: '11px', color: '#b45309', lineHeight: 1.5,
                    }}>
                      <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        Mensagens geradas por IA só são entregues no WhatsApp Business
                        se o contato estiver dentro da janela de 24h (tiver enviado uma
                        mensagem nas últimas 24 horas). Fora dessa janela, a mensagem será
                        pulada silenciosamente — não há fallback de template para conteúdo IA.
                      </span>
                    </div>
                  )}
                </div>
              ) : isBusinessChannel && msg.message_type === 'template' && templateUi ? (
                <TemplateMessageField
                  templates={approvedTemplates}
                  templateName={msg.template_name || ''}
                  templateLanguage={msg.template_language || ''}
                  onTemplateChange={(tpl) => {
                    updateMessage(idx, 'template_name', tpl?.name || '');
                    updateMessage(idx, 'template_language', tpl?.language || 'pt_BR');
                  }}
                  values={valuesFromMessage(msg)}
                  onValuesChange={(vals) => {
                    updateMessage(idx, 'template_params', vals.body_params);
                    if (followupCaps.header || followupCaps.media) updateMessage(idx, 'header_param', vals.header_param);
                    if (followupCaps.buttons) updateMessage(idx, 'button_params', vals.button_params);
                  }}
                  capabilities={followupCaps}
                  colors={colors}
                />
              ) : isBusinessChannel && msg.message_type === 'template' ? (
                <div>
                  <select
                    value={msg.template_name || ''}
                    onChange={(e) => updateMessage(idx, 'template_name', e.target.value)}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', outline: 'none',
                    }}
                  >
                    <option value="">Selecione um template...</option>
                    {approvedTemplates.map(t => (
                      <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                    ))}
                  </select>
                  {approvedTemplates.length === 0 && (
                    <p style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px' }}>
                      Nenhum template aprovado. Crie templates na aba Templates.
                    </p>
                  )}
                  {/* Variáveis do template escolhido: valores resolvidos POR
                      destinatário no envio (aceitam {{primeiro_nome}} etc.). */}
                  {(() => {
                    const tpl = approvedTemplates.find(t => t.name === msg.template_name);
                    const body = (tpl?.components || []).find(
                      c => (c.type || '').toUpperCase() === 'BODY'
                    )?.text || '';
                    const params = positionalParamsInText(body);
                    if (!params.length) return null;
                    const values = msg.template_params || [];
                    return (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {params.map((n, pi) => (
                          <input
                            key={n}
                            value={values[pi] || ''}
                            onChange={(e) => {
                              const next = [...values];
                              next[pi] = e.target.value;
                              updateMessage(idx, 'template_params', next);
                            }}
                            placeholder={`Valor do {{${n}}} — ex.: ${n === 1 ? '{{primeiro_nome}}' : 'texto ou {{campo:Nome}}'}`}
                            style={{
                              width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                              border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                              padding: '8px 10px', fontSize: '12px', outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        ))}
                        <p style={{ fontSize: '11px', color: colors.textTertiary, margin: 0 }}>
                          Cada contato recebe o próprio valor. Variável sem valor bloqueia
                          só o envio daquele contato, com o motivo no histórico.
                        </p>
                        <div style={{
                          fontSize: '12px', color: colors.textPrimary, whiteSpace: 'pre-wrap',
                          backgroundColor: colors.bgSecondary, borderRadius: '8px',
                          padding: '8px 10px', border: `1px solid ${colors.borderColor}`,
                          lineHeight: 1.45,
                        }}>
                          {fillBodyText(body, values)}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : isBusinessChannel && msg.message_type === 'interactive' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Sub-type toggle: Botões vs Lista */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => updateMessage(idx, 'interactive_type', 'button')}
                      style={{
                        flex: 1, padding: '5px 10px', borderRadius: '6px', fontSize: '11px',
                        fontWeight: 500, cursor: 'pointer', border: `1px solid ${colors.borderColor}`,
                        backgroundColor: (msg.interactive_type || 'button') === 'button' ? colors.accentPrimary : colors.bgPrimary,
                        color: (msg.interactive_type || 'button') === 'button' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Botões (max 3)
                    </button>
                    <button
                      onClick={() => updateMessage(idx, 'interactive_type', 'list')}
                      style={{
                        flex: 1, padding: '5px 10px', borderRadius: '6px', fontSize: '11px',
                        fontWeight: 500, cursor: 'pointer', border: `1px solid ${colors.borderColor}`,
                        backgroundColor: msg.interactive_type === 'list' ? colors.accentPrimary : colors.bgPrimary,
                        color: msg.interactive_type === 'list' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Lista/Menu (max 10)
                    </button>
                  </div>

                  {/* Body text (required) */}
                  <div>
                    <textarea
                      ref={(el) => { textRefs.current[`${idx}:text`] = el; }}
                      value={msg.text || ''}
                      onChange={(e) => updateMessage(idx, 'text', e.target.value)}
                      placeholder="Texto da mensagem (obrigatório)"
                      rows={3}
                      maxLength={1024}
                      style={{
                        width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                        fontFamily: 'inherit', boxSizing: 'border-box',
                        wordBreak: 'break-word', overflowWrap: 'break-word',
                      }}
                    />
                    <span style={{ fontSize: '10px', color: colors.textTertiary }}>{(msg.text || '').length}/1024</span>
                    <VariableChips
                      onInsert={(t) => insertVariable(idx, 'text', t)}
                      showCustomFieldHint={false}
                      style={{ marginTop: 6 }}
                    />
                  </div>

                  {/* ===== BUTTON EDITOR ===== */}
                  {(msg.interactive_type || 'button') === 'button' && (
                    <div style={{
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', backgroundColor: colors.bgPrimary,
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                        Botões de Resposta ({(msg.buttons || []).length}/3)
                      </div>
                      {(msg.buttons || []).map((btn, bi) => (
                        <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <input
                            value={btn.title || ''}
                            onChange={(e) => {
                              const newButtons = [...(msg.buttons || [])];
                              newButtons[bi] = { ...newButtons[bi], title: e.target.value };
                              updateMessage(idx, 'buttons', newButtons);
                            }}
                            placeholder={`Botão ${bi + 1}`}
                            maxLength={20}
                            style={{
                              flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                              border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                              padding: '6px 8px', fontSize: '12px', outline: 'none',
                            }}
                          />
                          <span style={{ fontSize: '10px', color: colors.textTertiary, minWidth: '30px' }}>
                            {(btn.title || '').length}/20
                          </span>
                          <button
                            onClick={() => {
                              const newButtons = (msg.buttons || []).filter((_, i) => i !== bi);
                              updateMessage(idx, 'buttons', newButtons);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      {(msg.buttons || []).length < 3 && (
                        <button
                          onClick={() => {
                            const newButtons = [...(msg.buttons || []), { id: `btn_${(msg.buttons || []).length + 1}`, title: '' }];
                            updateMessage(idx, 'buttons', newButtons);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                            color: colors.accentPrimary, border: `1px dashed ${colors.accentPrimary}40`,
                            borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', width: '100%',
                            justifyContent: 'center',
                          }}
                        >
                          <Plus size={12} /> Adicionar botão
                        </button>
                      )}
                    </div>
                  )}

                  {/* ===== LIST EDITOR (Simplified) ===== */}
                  {msg.interactive_type === 'list' && (() => {
                    const sections = msg.list_sections || [{ title: 'Opções', rows: [] }];
                    const allRows = sections[0]?.rows || [];
                    const totalRows = sections.reduce((sum, s) => sum + (s.rows || []).length, 0);
                    return (
                      <div style={{
                        border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        padding: '10px', backgroundColor: colors.bgPrimary,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                            Opções do Menu ({totalRows}/10)
                          </span>
                        </div>

                        {/* Simplified rows - title only */}
                        {allRows.map((row, ri) => (
                          <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <input
                              value={row.title || ''}
                              onChange={(e) => {
                                const newSections = [...sections];
                                const newRows = [...(newSections[0].rows || [])];
                                newRows[ri] = { ...newRows[ri], title: e.target.value };
                                newSections[0] = { ...newSections[0], rows: newRows };
                                updateMessage(idx, 'list_sections', newSections);
                              }}
                              placeholder={`Opção ${ri + 1}`}
                              maxLength={24}
                              style={{
                                flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                padding: '6px 8px', fontSize: '12px', outline: 'none',
                              }}
                            />
                            <button
                              onClick={() => {
                                const newSections = [...sections];
                                const newRows = (newSections[0].rows || []).filter((_, i) => i !== ri);
                                newSections[0] = { ...newSections[0], rows: newRows };
                                updateMessage(idx, 'list_sections', newSections);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}

                        {totalRows < 10 && (
                          <button
                            onClick={() => {
                              const newSections = [...sections];
                              if (!newSections[0]) newSections[0] = { title: 'Opções', rows: [] };
                              const newRows = [...(newSections[0].rows || []), { id: `row_${totalRows + 1}`, title: '', description: '' }];
                              newSections[0] = { ...newSections[0], rows: newRows };
                              updateMessage(idx, 'list_sections', newSections);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                              color: colors.accentPrimary, border: `1px dashed ${colors.accentPrimary}40`,
                              borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', width: '100%',
                              justifyContent: 'center',
                            }}
                          >
                            <Plus size={12} /> Adicionar opção
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* ===== ADVANCED OPTIONS TOGGLE ===== */}
                  <div>
                    <button
                      onClick={() => updateMessage(idx, '_showAdvanced', !msg._showAdvanced)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                        color: colors.textTertiary, border: 'none', padding: '4px 0',
                        fontSize: '11px', cursor: 'pointer',
                      }}
                    >
                      {msg._showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      Opções avançadas
                    </button>
                    {msg._showAdvanced && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px',
                        padding: '10px', border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        backgroundColor: colors.bgPrimary,
                      }}>
                        <input
                          value={msg.header_text || ''}
                          onChange={(e) => updateMessage(idx, 'header_text', e.target.value)}
                          placeholder="Cabeçalho (opcional, max 60)"
                          maxLength={60}
                          style={{
                            width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                            border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                            padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        <input
                          value={msg.footer_text || ''}
                          onChange={(e) => updateMessage(idx, 'footer_text', e.target.value)}
                          placeholder="Rodapé (opcional, max 60)"
                          maxLength={60}
                          style={{
                            width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                            border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                            padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        {msg.interactive_type === 'list' && (
                          <>
                            <input
                              value={msg.list_button_text || 'Ver opções'}
                              onChange={(e) => updateMessage(idx, 'list_button_text', e.target.value)}
                              placeholder="Texto do botão do menu (max 20)"
                              maxLength={20}
                              style={{
                                width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                            {/* Row descriptions */}
                            {((msg.list_sections || [])[0]?.rows || []).map((row, ri) => (
                              row.title ? (
                                <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '11px', color: colors.textSecondary, minWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {row.title}:
                                  </span>
                                  <input
                                    value={row.description || ''}
                                    onChange={(e) => {
                                      const newSections = [...(msg.list_sections || [])];
                                      const newRows = [...(newSections[0].rows || [])];
                                      newRows[ri] = { ...newRows[ri], description: e.target.value };
                                      newSections[0] = { ...newSections[0], rows: newRows };
                                      updateMessage(idx, 'list_sections', newSections);
                                    }}
                                    placeholder="Descrição (opcional)"
                                    maxLength={72}
                                    style={{
                                      flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                      border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                      padding: '4px 8px', fontSize: '11px', outline: 'none',
                                    }}
                                  />
                                </div>
                              ) : null
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Text */}
                  <textarea
                    ref={(el) => { textRefs.current[`${idx}:text`] = el; }}
                    value={msg.text || ''}
                    onChange={(e) => updateMessage(idx, 'text', e.target.value)}
                    placeholder="Texto da mensagem (opcional se tiver arquivo)"
                    rows={3}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                      fontFamily: 'inherit', boxSizing: 'border-box',
                      wordBreak: 'break-word', overflowWrap: 'break-word',
                    }}
                  />
                  <VariableChips
                    onInsert={(t) => insertVariable(idx, 'text', t)}
                    style={{ marginTop: 6 }}
                  />

                  {/* Files */}
              <div style={{ marginTop: '8px' }}>
                {msg.files.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {msg.files.map((f, fi) => (
                      <div key={fi} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        backgroundColor: colors.bgPrimary, padding: '4px 10px',
                        borderRadius: '6px', fontSize: '12px', color: colors.textSecondary,
                        border: `1px solid ${colors.borderColor}`,
                      }}>
                        <FileTypeIcon type={f.type} size={14} />
                        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.filename}
                        </span>
                        <button onClick={() => removeFile(idx, fi)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ef4444', padding: '2px', display: 'flex',
                        }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={el => fileInputRefs.current[idx] = el}
                  type="file"
                  multiple
                  accept={fileUploadService.getAcceptString()}
                  onChange={(e) => {
                    if (e.target.files.length > 0) handleFileUpload(idx, Array.from(e.target.files));
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRefs.current[idx]?.click()}
                  disabled={uploading[`msg-${idx}`]}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    backgroundColor: 'transparent', color: colors.accentPrimary,
                    border: `1px dashed ${colors.accentPrimary}40`, borderRadius: '6px',
                    padding: '6px 12px', fontSize: '12px', cursor: 'pointer', width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {uploading[`msg-${idx}`] ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading[`msg-${idx}`] ? 'Enviando...' : 'Adicionar arquivos'}
                </button>
              </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
        }}>
          <button
            onClick={addMessage}
            disabled={localMessages.length >= 20}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: 'transparent', color: localMessages.length >= 20 ? colors.textTertiary : colors.accentPrimary,
              border: `1px solid ${localMessages.length >= 20 ? colors.borderColor : colors.accentPrimary}`,
              borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: localMessages.length >= 20 ? 'default' : 'pointer',
            }}
          >
            <Plus size={16} /> Mensagem
          </button>
          <button onClick={handleSave} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}>
            <Check size={16} /> {dayNumber === 0 ? 'Salvar' : 'Salvar dia'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// SUB: Contact Picker Modal
// ============================================================
const ContactPickerModal = ({ isOpen, onClose, onSelect, colors, isBusinessChannel }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSelected([]);
      setSearch('');

      const fetchContacts = isBusinessChannel
        ? waBusinessService.getChats(200, 0).then(data => {
            const chatList = data?.data || data || [];
            return Array.isArray(chatList) ? chatList : [];
          })
        : whatsappService.getChats().then(data => {
            const chatList = data?.data || data || [];
            return Array.isArray(chatList) ? chatList : [];
          });

      fetchContacts
        .then(list => setChats(list))
        .catch(() => setChats([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, isBusinessChannel]);

  if (!isOpen) return null;

  const getNumber = (chat) => {
    if (isBusinessChannel) {
      return chat.contact_phone || '';
    }
    const jid = chat.remoteJid || chat.id || '';
    return jid.replace('@s.whatsapp.net', '').replace('@g.us', '');
  };

  const getName = (chat) => {
    if (isBusinessChannel) {
      return chat.contact_name || chat.contact_phone || '';
    }
    return chat.pushName || chat.name || chat.contactName || getNumber(chat);
  };

  const filtered = chats.filter(c => {
    if (isBusinessChannel) {
      if (!c.contact_phone) return false;
    } else {
      if (!c.remoteJid || c.remoteJid.endsWith('@g.us')) return false;
    }
    const s = search.toLowerCase();
    return !s || getName(c).toLowerCase().includes(s) || getNumber(c).includes(s);
  });

  const toggleSelect = (number) => {
    setSelected(prev => prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1001,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '440px', maxHeight: '70vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Selecionar contatos
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '12px 20px' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar contato..."
            style={{
              width: '100%', padding: '8px 12px', backgroundColor: colors.bgSecondary,
              color: colors.textPrimary, border: `1px solid ${colors.borderColor}`,
              borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: colors.textSecondary }}>
              <Loader2 size={20} /> Carregando...
            </div>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '24px' }}>
              Nenhum contato encontrado
            </p>
          ) : filtered.map((chat, i) => {
            const number = getNumber(chat);
            const isSelected = selected.includes(number);
            return (
              <div
                key={i}
                onClick={() => toggleSelect(number)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 0', borderBottom: `1px solid ${colors.borderColor}20`,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '4px',
                  border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
                  backgroundColor: isSelected ? colors.accentPrimary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <Check size={12} color="white" />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                    {getName(chat)}
                  </p>
                  <p style={{ margin: 0, fontSize: '11px', color: colors.textSecondary }}>
                    {number}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', backgroundColor: 'transparent',
            color: colors.textSecondary, border: `1px solid ${colors.borderColor}`,
            borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
          }}>
            Cancelar
          </button>
          <button
            onClick={() => { onSelect(selected); onClose(); }}
            disabled={selected.length === 0}
            style={{
              padding: '8px 16px', backgroundColor: selected.length === 0 ? colors.borderColor : colors.accentPrimary,
              color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
              fontWeight: 500, cursor: selected.length === 0 ? 'default' : 'pointer',
            }}
          >
            Adicionar {selected.length > 0 ? `(${selected.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const FollowUpsPage = ({ isNavOpen, onToggleNav }) => {
  const { colors } = useTheme();
  const { user } = useAuth();

  // View management
  const [view, setView] = useState('list'); // list | detail
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [editingDay, setEditingDay] = useState(null);
  const [editingScheduledMessages, setEditingScheduledMessages] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Plan limits state
  const [followupLimits, setFollowupLimits] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoPlans, setPromoPlans] = useState([]);
  const [promoSubtitle, setPromoSubtitle] = useState('');

  // Detail view state
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [history, setHistory] = useState([]);
  const [testNumber, setTestNumber] = useState('');
  const [sendingDay, setSendingDay] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showTestContactPicker, setShowTestContactPicker] = useState(false);
  const [detailTab, setDetailTab] = useState('details');

  /** Número do teste para a API, com DDI ('' enquanto não for válido).
   *  Máscara e regra em components/followups/followupPhone.js. */
  const getTestNumberRaw = () => followupPhoneToRaw(testNumber);
  const INVALID_TEST_NUMBER_MSG = 'Número inválido. Informe DDD + número, ou o número de outro país começando com + (ex.: +1 415 555 0123).';

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  // Visualização da lista: cards (padrão) ou tabela compacta, para conta com
  // muitos follow-ups. Preferência só deste navegador.
  const [listMode, setListMode] = useState(() => {
    try { return localStorage.getItem('infinitsales-followups-view') === 'table' ? 'table' : 'cards'; } catch { return 'cards'; }
  });
  const changeListMode = (mode) => {
    setListMode(mode);
    try { localStorage.setItem('infinitsales-followups-view', mode); } catch { /* ignore */ }
  };
  const [tableLimit, setTableLimit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  // WhatsApp Business templates
  const [approvedTemplates, setApprovedTemplates] = useState([]);

  const isBusinessChannel = user?.default_channel === 'wa_business';

  // ============================================
  // DATA LOADING
  // ============================================

  const loadFollowups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await followupService.list();
      setFollowups(res.data || []);
    } catch (err) {
      console.error('Error loading followups:', err);
      setError('Erro ao carregar follow-ups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFollowups();
    // Load followup limits
    followupService.getLimits().then(res => {
      console.log(res.data)
      setFollowupLimits(res.data || res);
    }).catch(() => {});
  }, [loadFollowups]);

  // Deep-link: quando chega via /followups?id=<id> (badge do inbox abre em
  // nova aba), abrimos automaticamente o detalhe do followup. Sem isso o
  // operador caia na lista e tinha que clicar de novo — passava a impressao
  // de que o followup nao tinha sido criado.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const deepLinkId = params.get('id');
    if (!deepLinkId) return;
    loadFollowupDetail(deepLinkId).then(() => {
      setDetailTab('details');
      setView('detail');
    });
    // Roda apenas no mount inicial — mudar de followup depois e via clique.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load approved templates for Business channel
  useEffect(() => {
    if (isBusinessChannel) {
      templateService.list()
        .then(data => {
          // Template com variáveis é aceito aqui: o editor pede os valores
          // (template_params) e o backend resolve por destinatário no envio.
          const approved = (data.templates || []).filter(t => t.status === 'APPROVED');
          setApprovedTemplates(approved);
        })
        .catch(err => console.error('Error loading templates:', err));
    }
  }, [isBusinessChannel]);

  const loadFollowupDetail = useCallback(async (id) => {
    try {
      const res = await followupService.getById(id);
      setSelectedFollowup(res.data);
      const histRes = await followupService.getHistory(id);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error loading followup detail:', err);
    }
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleCreateNew = () => {
    setShowTypeModal(true);
  };

  const openPromoModalWithMessage = async (subtitle) => {
    try {
      const plansRes = await subscriptionService.getPlans();
      const allPlans = plansRes.data || plansRes;
      setPromoPlans(allPlans.filter(p => p.product !== 'combo' && p.price_brl > 0));
    } catch {
      setPromoPlans([]);
    }
    setPromoSubtitle(subtitle);
    setShowPromoModal(true);
  };

  const handleCreateWithType = async (type) => {
    // Fetch fresh limits if not yet loaded
    let limits = followupLimits;
    if (!limits) {
      try {
        const res = await followupService.getLimits();
        limits = res.data || res;
        setFollowupLimits(limits);
      } catch {
        // If limits fetch fails, let backend enforce — proceed with creation
      }
    }

    // Check plan limits before creating
    if (limits) {
      if (type === 'challenge_sequence' && !limits.can_create_sequential) {
        setShowTypeModal(false);
        openPromoModalWithMessage('Follow-ups sequenciais estao disponiveis a partir do plano Pro. Faca upgrade para desbloquear.');
        return;
      }
      if (type === 'scheduled_message' && limits.max_allowed != null && limits.current_count >= limits.max_allowed) {
        setShowTypeModal(false);
        openPromoModalWithMessage(`Voce atingiu o limite de ${limits.max_allowed} follow-ups do plano ${(limits.tier || 'sales_free').replace('sales_', '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}. Faca upgrade para criar mais.`);
        return;
      }
    }

    setShowTypeModal(false);
    setSaving(true);
    try {
      const baseConfig = isBusinessChannel ? {
        fallback_template_name: '',
        fallback_template_language: 'pt_BR',
        retry_interval_hours: 6,
        max_retries: 3,
      } : null;

      let payload;
      if (type === 'scheduled_message') {
        payload = {
          name: 'Nova Mensagem Agendada',
          description: '',
          type: 'scheduled_message',
          total_days: 0,
          recipients: [],
          days: {},
          messages: [],
          schedule: {
            send_time: '09:00',
            recurrence: 'once',
            recurrence_days: [],
            recurrence_day_of_month: null,
            start_date: new Date().toISOString().split('T')[0],
            end_date: null,
          },
          channel: isBusinessChannel ? 'wa_business' : 'wa_personal',
          business_config: baseConfig,
        };
      } else {
        payload = {
          name: 'Novo Follow-up',
          description: '',
          total_days: 0,
          recipients: [],
          days: {},
          type: 'challenge_sequence',
          channel: isBusinessChannel ? 'wa_business' : 'wa_personal',
          business_config: baseConfig,
        };
      }

      const res = await followupService.create(payload);
      const newId = res.data?.id || res.data;
      await loadFollowupDetail(newId);
      await loadFollowups();
      // Refresh limits after creation
      followupService.getLimits().then(r => setFollowupLimits(r.data || r)).catch(() => {});
      setDetailTab('details');
      setView('detail');
    } catch (err) {
      console.error('Error creating followup:', err);
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === 'followup_limit_reached') {
        openPromoModalWithMessage(detail.message || 'Faca upgrade para desbloquear mais follow-ups.');
      } else {
        alert('Erro ao criar follow-up: ' + (typeof detail === 'string' ? detail : detail?.message || err.message));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (followup) => {
    if (!window.confirm(`Excluir "${followup.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await followupService.delete(followup.id);
      await loadFollowups();
      // Refresh limits after deletion
      followupService.getLimits().then(r => setFollowupLimits(r.data || r)).catch(() => {});
    } catch (err) {
      console.error('Error deleting followup:', err);
    }
  };

  const handleStatusToggle = async (followup) => {
    try {
      if (followup.status === 'active') {
        await followupService.updateStatus(followup.id, 'paused');
      } else if (followup.status === 'paused') {
        await followupService.updateStatus(followup.id, 'active');
      }
      await loadFollowups();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleViewDetail = async (followup) => {
    await loadFollowupDetail(followup.id);
    setTestNumber('');
    setDetailTab('details');
    setView('detail');
  };

  const handleSendDay = async (day) => {
    const rawPhone = getTestNumberRaw();
    if (!rawPhone) {
      alert(testNumber.trim() ? INVALID_TEST_NUMBER_MSG : 'Informe o número para teste.');
      return;
    }
    setSendingDay(day);
    try {
      const res = await followupService.sendDay(selectedFollowup.id, day, [rawPhone], true);
      const data = res.data;
      if (data.skipped?.length > 0) {
        alert(`Dia ${day} já foi enviado para este número. Resete o histórico para reenviar.`);
      } else if (data.failed?.length > 0) {
        alert(`Erro ao enviar algumas mensagens do dia ${day}.`);
      } else {
        alert(`Dia ${day} enviado com sucesso!`);
      }
      // Refresh history
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingDay(null);
    }
  };

  const handleActivate = async () => {
    try {
      await followupService.updateStatus(selectedFollowup.id, 'active');
      alert('Follow-up ativado com sucesso!');
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeactivate = async () => {
    try {
      await followupService.updateStatus(selectedFollowup.id, 'paused');
      alert('Follow-up pausado.');
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleResetDay = async (day) => {
    if (!window.confirm(`Resetar histórico do dia ${day}? Isso permitirá reenviar.`)) return;
    try {
      await followupService.resetHistoryByDay(selectedFollowup.id, day);
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error resetting day:', err);
    }
  };

  const handleResetRecipient = async (phone) => {
    if (!window.confirm(`Resetar todo histórico do número ${phone}?`)) return;
    try {
      await followupService.resetHistoryByRecipient(selectedFollowup.id, phone);
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error resetting recipient:', err);
    }
  };

  const handleUpdateFollowup = async (partialData) => {
    try {
      await followupService.update(selectedFollowup.id, partialData);
      await loadFollowupDetail(selectedFollowup.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === 'followup_limit_reached') {
        // Revert the schedule change in UI by reloading
        await loadFollowupDetail(selectedFollowup.id);
        openPromoModalWithMessage(detail.message || 'Faca upgrade para desbloquear mais follow-ups.');
      } else {
        alert('Erro ao atualizar: ' + (typeof detail === 'string' ? detail : detail?.message || err.message));
      }
    }
  };

  const handleRemoveRecipient = async (phone) => {
    try {
      const res = await followupService.removeRecipient(selectedFollowup.id, phone);
      const payload = res?.data || res || {};
      // Quando o ultimo recipient sai, o backend deleta o followup inteiro —
      // sem isso, recarregar o detalhe daria 404 e a tela ficaria com dados
      // antigos. Voltamos pra lista e atualizamos os contadores de plano.
      if (payload.followup_deleted) {
        setSelectedFollowup(null);
        setView('list');
        await loadFollowups();
        followupService.getLimits().then(r => setFollowupLimits(r.data || r)).catch(() => {});
        return;
      }
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDetailDayEditorSave = async (dayNumber, messages) => {
    try {
      const updatedDays = { ...selectedFollowup.days };
      updatedDays[String(dayNumber)] = { messages };
      await followupService.update(selectedFollowup.id, { days: updatedDays });
      await loadFollowupDetail(selectedFollowup.id);
      setEditingDay(null);
    } catch (err) {
      alert('Erro ao salvar dia: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddDay = async (newDayNumber) => {
    try {
      await followupService.update(selectedFollowup.id, { total_days: newDayNumber });
      await loadFollowupDetail(selectedFollowup.id);
      setEditingDay(newDayNumber);
    } catch (err) {
      alert('Erro ao adicionar dia: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveDay = async (dayNumber) => {
    const totalDays = selectedFollowup.total_days;
    if (!window.confirm(`Remover o dia ${dayNumber}? As mensagens configuradas neste dia serão perdidas.`)) return;
    try {
      const currentDays = { ...selectedFollowup.days };
      // Re-index: shift all days after the removed one down by 1
      const newDays = {};
      for (let d = 1; d <= totalDays; d++) {
        if (d < dayNumber) {
          // Days before removed day stay the same
          if (currentDays[String(d)]) newDays[String(d)] = currentDays[String(d)];
        } else if (d > dayNumber) {
          // Days after removed day shift down by 1
          if (currentDays[String(d)]) newDays[String(d - 1)] = currentDays[String(d)];
        }
        // d === dayNumber is skipped (removed)
      }
      await followupService.update(selectedFollowup.id, {
        total_days: totalDays - 1,
        days: newDays,
      });
      await loadFollowupDetail(selectedFollowup.id);
    } catch (err) {
      alert('Erro ao remover dia: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Scheduled message handlers
  const handleSendNow = async () => {
    const rawPhone = getTestNumberRaw();
    if (!rawPhone && testNumber.trim()) {
      alert(INVALID_TEST_NUMBER_MSG);
      return;
    }
    if (!rawPhone || !selectedFollowup) return;
    setSendingTest(true);
    try {
      // Use the send-day endpoint with day=0 and the test number (reuses existing backend for manual test)
      const res = await followupService.sendDay(selectedFollowup.id, 0, [rawPhone], true);
      const data = res.data;
      if (data.failed?.length > 0) {
        alert('Erro ao enviar algumas mensagens.');
      } else {
        alert('Mensagens enviadas com sucesso!');
      }
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      // If send-day doesn't work for day=0, try send-now
      try {
        await followupService.sendNow(selectedFollowup.id);
        alert('Mensagens enviadas com sucesso!');
        const histRes = await followupService.getHistory(selectedFollowup.id);
        setHistory(histRes.data || []);
      } catch (err2) {
        alert('Erro: ' + (err2.response?.data?.detail || err.response?.data?.detail || err.message));
      }
    } finally {
      setSendingTest(false);
    }
  };

  const handleScheduledMessagesSave = async (messages) => {
    try {
      await followupService.update(selectedFollowup.id, { messages });
      await loadFollowupDetail(selectedFollowup.id);
      setEditingScheduledMessages(false);
    } catch (err) {
      alert('Erro ao salvar mensagens: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddRecipientsToFollowup = async (numbers) => {
    if (!selectedFollowup) return;
    const existing = selectedFollowup.recipients || [];
    // Extract existing phone strings for dedup (handle both legacy and new format)
    const existingPhones = new Set(
      existing.map(r => typeof r === 'string' ? r : r?.phone).filter(Boolean)
    );
    // Only add truly new numbers (backend will set added_at = today for new ones)
    const newNumbers = numbers.filter(n => !existingPhones.has(n));
    if (newNumbers.length === 0) return;
    const merged = [...existing, ...newNumbers];
    try {
      await followupService.update(selectedFollowup.id, { recipients: merged });
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro ao adicionar destinatários: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Filtered followups
  const filteredFollowups = followups.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!(f.name || '').toLowerCase().includes(s) && !f.description?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgSecondary, height: '100vh', overflow: 'hidden',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 24px', backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm, minHeight: '60px', zIndex: 1,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
    navToggleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '40px', height: '40px', border: 'none', background: 'transparent',
      color: colors.textSecondary, borderRadius: '8px', cursor: 'pointer',
    },
    headerIcon: {
      width: '40px', height: '40px', backgroundColor: colors.accentPrimary,
      borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
    },
    content: { flex: 1, overflow: 'auto', padding: '20px 24px' },
    card: {
      backgroundColor: colors.bgPrimary, borderRadius: '12px', padding: '16px',
      border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
      transition: 'border-color 0.15s',
    },
    btn: {
      display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
      borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none',
    },
    btnPrimary: { backgroundColor: colors.accentPrimary, color: 'white' },
    btnOutline: { backgroundColor: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.borderColor}` },
    input: {
      width: '100%', padding: '10px 12px', backgroundColor: colors.bgPrimary,
      color: colors.textPrimary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    },
  };

  // ============================================
  // RENDER: LIST VIEW
  // ============================================

  const STATUS_LABELS = {
    all: 'Todos', draft: 'Rascunho', active: 'Ativo', paused: 'Pausado', completed: 'Concluído',
  };
  const statusCounts = followups.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {});

  // Linha "programação" de um follow-up, igual no card e na tabela.
  const scheduleSummary = (f) => {
    if (f.type === 'scheduled_message') {
      const sc = f.schedule || {};
      const rec = sc.recurrence === 'daily' ? 'Diário'
        : sc.recurrence === 'weekly' ? 'Semanal'
          : sc.recurrence === 'monthly' ? 'Mensal' : 'Uma vez';
      const n = f.messages_summary?.message_count || 0;
      return `${sc.send_time || '09:00'} · ${rec} · ${n} ${n === 1 ? 'mensagem' : 'mensagens'}`;
    }
    const configured = Object.keys(f.days_summary || {}).filter(
      (k) => (f.days_summary[k]?.message_count || 0) > 0,
    ).length;
    return `${f.total_days} dias · ${configured} configurados`;
  };

  const renderTableView = () => {
    const rows = filteredFollowups.slice(0, tableLimit);
    const th = {
      textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700,
      color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.4px',
      borderBottom: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
      whiteSpace: 'nowrap', position: 'sticky', top: 0,
    };
    const td = {
      padding: '9px 14px', fontSize: 13, color: colors.textPrimary,
      borderBottom: `1px solid ${colors.borderColor}`, verticalAlign: 'middle',
    };
    return (
      <div style={{
        backgroundColor: colors.bgPrimary, border: `1px solid ${colors.borderColor}`,
        borderRadius: 12, overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={th}>Follow-up</th>
                <th style={th}>Tipo</th>
                <th style={th}>Status</th>
                <th style={th}>Programação</th>
                <th style={{ ...th, textAlign: 'right' }}>Destinatários</th>
                <th style={{ ...th, width: 92 }} aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const isScheduled = f.type === 'scheduled_message';
                return (
                  <tr
                    key={f.id}
                    onClick={() => handleViewDetail(f)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.name}
                      </div>
                      {f.description && (
                        <div style={{
                          fontSize: 12, color: colors.textSecondary, marginTop: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {f.description}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: colors.textSecondary }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {isScheduled ? <Clock size={13} /> : <Calendar size={13} />}
                        {isScheduled ? 'Agendada' : 'Sequência'}
                      </span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <StatusBadge status={f.status} colors={colors} />
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: colors.textSecondary }}>
                      {scheduleSummary(f)}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {(f.recipients || []).length}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        {(f.status === 'active' || f.status === 'paused') && (
                          <button
                            onClick={() => handleStatusToggle(f)}
                            title={f.status === 'active' ? 'Pausar' : 'Retomar'}
                            aria-label={f.status === 'active' ? `Pausar ${f.name}` : `Retomar ${f.name}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
                              border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent',
                              color: colors.textSecondary,
                            }}
                          >
                            {f.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(f)}
                          title="Excluir"
                          aria-label={`Excluir ${f.name}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
                            border: '1px solid #ef444430', backgroundColor: 'transparent', color: '#ef4444',
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredFollowups.length > tableLimit && (
          <div style={{ padding: 10, textAlign: 'center', borderTop: `1px solid ${colors.borderColor}` }}>
            <button
              onClick={() => setTableLimit((n) => n + 50)}
              style={{ ...styles.btn, ...styles.btnOutline, display: 'inline-flex' }}
            >
              Mostrar mais ({filteredFollowups.length - tableLimit} restantes)
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderListView = () => (
    <>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['all', 'draft', 'active', 'paused', 'completed'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 500,
                border: 'none', cursor: 'pointer',
                backgroundColor: statusFilter === s ? colors.accentPrimary : `${colors.textSecondary}15`,
                color: statusFilter === s ? 'white' : colors.textSecondary,
              }}
            >
              {STATUS_LABELS[s]}
              {' '}
              <span style={{ opacity: 0.75 }}>{s === 'all' ? followups.length : (statusCounts[s] || 0)}</span>
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label="Visualização"
          style={{
            marginLeft: 'auto', display: 'inline-flex', gap: 2, padding: 3,
            borderRadius: 10, backgroundColor: colors.bgTertiary,
          }}
        >
          {[
            { key: 'cards', label: 'Cards', icon: LayoutGrid },
            { key: 'table', label: 'Tabela', icon: List },
          ].map((m) => {
            const on = listMode === m.key;
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => changeListMode(m.key)}
                aria-pressed={on}
                title={`Ver como ${m.label.toLowerCase()}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12.5,
                  cursor: 'pointer', fontWeight: on ? 600 : 500,
                  backgroundColor: on ? colors.bgPrimary : 'transparent',
                  color: on ? colors.accentPrimary : colors.textSecondary,
                }}
              >
                <Icon size={14} /> {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cards grid */}
      {filteredFollowups.length === 0 && !loading ? (
        <EmptyState colors={colors} onCreateNew={handleCreateNew} />
      ) : listMode === 'table' ? renderTableView() : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filteredFollowups.map(f => {
            const isScheduled = f.type === 'scheduled_message';
            const daysConfigured = !isScheduled ? Object.keys(f.days_summary || {}).filter(
              k => (f.days_summary[k]?.message_count || 0) > 0
            ).length : 0;
            const msgCount = isScheduled ? (f.messages_summary?.message_count || 0) : 0;
            const scheduleInfo = isScheduled ? (f.schedule || {}) : null;
            const recurrenceLabel = scheduleInfo ? (
              scheduleInfo.recurrence === 'daily' ? 'Diário' :
              scheduleInfo.recurrence === 'weekly' ? 'Semanal' :
              scheduleInfo.recurrence === 'monthly' ? 'Mensal' : 'Uma vez'
            ) : '';

            return (
              <div key={f.id} style={styles.card} onClick={() => handleViewDetail(f)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      {isScheduled && (
                        <span style={{
                          padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                          backgroundColor: `${colors.accentPrimary}15`, color: colors.accentPrimary,
                        }}>
                          AGENDADA
                        </span>
                      )}
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
                        {f.name}
                      </h4>
                    </div>
                    {f.description && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textSecondary }}>
                        {f.description.slice(0, 60)}{f.description.length > 60 ? '...' : ''}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={f.status} colors={colors} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: colors.textSecondary }}>
                  {isScheduled ? (
                    <>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={13} /> {scheduleInfo?.send_time || '09:00'}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MessageSquare size={13} /> {msgCount} msg{msgCount !== 1 ? 's' : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {recurrenceLabel}
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={13} /> {f.total_days} dias
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FileText size={13} /> {daysConfigured} configurados
                      </span>
                    </>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Users size={13} /> {(f.recipients || []).length}
                  </span>
                </div>

                {/* Card actions */}
                <div
                  style={{ display: 'flex', gap: '6px', marginTop: '12px', borderTop: `1px solid ${colors.borderColor}`, paddingTop: '10px' }}
                  onClick={e => e.stopPropagation()}
                >
                  {(f.status === 'active' || f.status === 'paused') && (
                    <button onClick={() => handleStatusToggle(f)} style={{ ...styles.btn, ...styles.btnOutline, padding: '6px 10px', fontSize: '12px' }}>
                      {f.status === 'active' ? <><Pause size={13} /> Pausar</> : <><Play size={13} /> Retomar</>}
                    </button>
                  )}
                  <button onClick={() => handleDelete(f)} style={{ ...styles.btn, padding: '6px 10px', fontSize: '12px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef444430' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ============================================
  // RENDER: DETAIL VIEW
  // ============================================

  const renderDetailView = () => {
    if (!selectedFollowup) return null;

    const tabs = [
      { key: 'details', label: 'Detalhes' },
      { key: 'recipients', label: 'Destinatários' },
      { key: 'history', label: 'Histórico de envio' },
    ];

    return (
      <>
        {/* Tab Bar (pill style) */}
        <div style={{
          display: 'flex', gap: '6px', marginBottom: '20px',
          overflowX: 'auto', paddingBottom: '2px',
        }}>
          {tabs.map(tab => {
            const isActive = detailTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setDetailTab(tab.key)}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  backgroundColor: isActive ? colors.accentPrimary : colors.bgTertiary,
                  color: isActive ? 'white' : colors.textSecondary,
                  borderRadius: '18px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {detailTab === 'details' && (
          selectedFollowup.type === 'scheduled_message' ? (
            <ScheduledMessageDetailsTab
              followup={selectedFollowup}
              colors={colors}
              styles={styles}
              testNumber={testNumber}
              setTestNumber={setTestNumber}
              sendingTest={sendingTest}
              onSendNow={handleSendNow}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
              onUpdateFollowup={handleUpdateFollowup}
              onOpenMessageEditor={() => setEditingScheduledMessages(true)}
              isBusinessChannel={isBusinessChannel}
              approvedTemplates={approvedTemplates}
              onOpenContactPicker={() => setShowTestContactPicker(true)}
              canCreateRecurring={followupLimits?.can_create_recurring ?? true}
            />
          ) : (
            <DetailsTab
              followup={selectedFollowup}
              history={history}
              colors={colors}
              styles={styles}
              testNumber={testNumber}
              setTestNumber={setTestNumber}
              sendingDay={sendingDay}
              onSendDay={handleSendDay}
              onResetDay={handleResetDay}
              onActivate={handleActivate}
              onDeactivate={handleDeactivate}
              onUpdateFollowup={handleUpdateFollowup}
              onOpenDayEditor={(dayNum) => setEditingDay(dayNum)}
              onAddDay={handleAddDay}
              onRemoveDay={handleRemoveDay}
              isBusinessChannel={isBusinessChannel}
              approvedTemplates={approvedTemplates}
              onOpenContactPicker={() => setShowTestContactPicker(true)}
            />
          )
        )}

        {detailTab === 'recipients' && (
          <RecipientsTab
            followup={selectedFollowup}
            history={history}
            colors={colors}
            styles={styles}
            onRemoveRecipient={handleRemoveRecipient}
            onRefresh={() => loadFollowupDetail(selectedFollowup.id)}
            onAddRecipients={handleAddRecipientsToFollowup}
            onOpenContactPicker={() => setShowContactPicker(true)}
            onResetRecipient={handleResetRecipient}
            onResetDay={handleResetDay}
          />
        )}

        {detailTab === 'history' && (
          <HistoryTab
            history={history}
            colors={colors}
            styles={styles}
            onResetRecipient={handleResetRecipient}
            isScheduledMessage={selectedFollowup?.type === 'scheduled_message'}
          />
        )}
      </>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  // Business channel placeholder removed — now fully functional

  const getHeaderInfo = () => {
    switch (view) {
      case 'detail': return { title: selectedFollowup?.name || 'Detalhe', subtitle: 'Detalhe e teste' };
      default: return { title: 'Follow-ups', subtitle: `${followups.length} follow-up${followups.length !== 1 ? 's' : ''}` };
    }
  };

  const headerInfo = getHeaderInfo();

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {onToggleNav && (
            <button onClick={onToggleNav} style={styles.navToggleBtn}>
              {isNavOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
          )}
          {view !== 'list' && (
            <button
              onClick={() => { setView('list'); loadFollowups(); }}
              style={{ ...styles.navToggleBtn, color: colors.textPrimary }}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <div style={styles.headerIcon}><Zap size={20} /></div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
              {headerInfo.title}
            </h2>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>{headerInfo.subtitle}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {view === 'list' && (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                backgroundColor: colors.bgTertiary, borderRadius: 20,
                padding: '0 12px', height: 38, width: 300,
              }}>
                <Search size={16} style={{ color: colors.textSecondary }} />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Buscar follow-up..."
                  style={{
                    flex: 1, border: 'none', background: 'transparent',
                    padding: '8px 4px', fontSize: 14, color: colors.textPrimary, outline: 'none',
                  }}
                />
              </div>
              <button onClick={handleCreateNew} disabled={saving} style={{ ...styles.btn, ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? <Loader2 size={16} /> : <Plus size={16} />} Novo Follow-up
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading && view === 'list' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: colors.textSecondary }}>
            <Loader2 size={24} /> <span style={{ marginLeft: '8px' }}>Carregando...</span>
          </div>
        ) : (
          <>
            {view === 'list' && renderListView()}
            {view === 'detail' && renderDetailView()}
          </>
        )}
      </div>

      {/* Day Editor Modal */}
      <DayEditorModal
        isOpen={editingDay !== null}
        dayNumber={editingDay}
        messages={editingDay ? (selectedFollowup?.days?.[String(editingDay)]?.messages || []) : []}
        onSave={(messages) => handleDetailDayEditorSave(editingDay, messages)}
        onClose={() => setEditingDay(null)}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
        approvedTemplates={approvedTemplates}
        canCreateAiGenerated={followupLimits?.can_create_ai_generated ?? false}
      />

      {/* Scheduled Message Editor Modal (reuses DayEditorModal) */}
      <DayEditorModal
        isOpen={editingScheduledMessages}
        dayNumber={0}
        messages={selectedFollowup?.messages || []}
        onSave={handleScheduledMessagesSave}
        onClose={() => setEditingScheduledMessages(false)}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
        approvedTemplates={approvedTemplates}
        canCreateAiGenerated={followupLimits?.can_create_ai_generated ?? false}
      />

      {/* Contact Picker Modal (for Recipients tab - multi-select) */}
      <ContactPickerModal
        isOpen={showContactPicker}
        onClose={() => setShowContactPicker(false)}
        onSelect={handleAddRecipientsToFollowup}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
      />

      {/* Contact Picker Modal (for Test - single-select, sets testNumber) */}
      <ContactPickerModal
        isOpen={showTestContactPicker}
        onClose={() => setShowTestContactPicker(false)}
        onSelect={(phones) => {
          if (phones.length > 0) {
            // Número gravado do contato: sem re-máscara BR (14155550123
            // virava 5514155550123).
            setTestNumber(followupPhoneFromStored(phones[0]));
          }
        }}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
      />

      {/* Type Selection Modal */}
      {showTypeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: colors.bgPrimary, borderRadius: '16px',
            width: '100%', maxWidth: '460px', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                Novo Follow-up
              </h3>
              <button onClick={() => setShowTypeModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
                padding: '8px', borderRadius: '8px',
              }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
                Escolha o tipo de follow-up:
              </p>

              {/* Sequential option */}
              <button
                onClick={() => handleCreateWithType('challenge_sequence')}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '14px',
                  padding: '16px', borderRadius: '12px', cursor: 'pointer',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
                  textAlign: 'left', transition: 'all 0.15s', width: '100%',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = colors.accentPrimary; e.currentTarget.style.backgroundColor = `${colors.accentPrimary}08`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = colors.borderColor; e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
              >
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Calendar size={20} color={colors.accentPrimary} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Sequência de Dias
                    {followupLimits && !followupLimits.can_create_sequential && (
                      <span style={{
                        backgroundColor: colors.accentPrimary, color: 'white',
                        fontSize: '10px', fontWeight: 700, padding: '2px 8px',
                        borderRadius: '8px',
                      }}>PRO</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.4' }}>
                    Configure mensagens diferentes para cada dia. Os destinatários progridem do dia 1 ao último dia automaticamente.
                  </div>
                </div>
              </button>

              {/* Scheduled message option */}
              <button
                onClick={() => handleCreateWithType('scheduled_message')}
                disabled={saving}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '14px',
                  padding: '16px', borderRadius: '12px', cursor: 'pointer',
                  border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
                  textAlign: 'left', transition: 'all 0.15s', width: '100%',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = colors.accentPrimary; e.currentTarget.style.backgroundColor = `${colors.accentPrimary}08`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = colors.borderColor; e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
              >
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  backgroundColor: '#f59e0b15', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Clock size={20} color="#f59e0b" />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, marginBottom: '4px' }}>
                    Mensagem Agendada
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.4' }}>
                    Configure uma mensagem única para enviar pontualmente ou com recorrência (diária, semanal, mensal).
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PromoModal for plan upgrade */}
      <PromoModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        plans={promoPlans}
        title="Faca upgrade para desbloquear"
        subtitle={promoSubtitle}
      />
    </div>
  );
};

export default FollowUpsPage;
